/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Switch,
  Alert,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

const { width } = Dimensions.get('window');

// JOBENG Logo component using the app icon
const CompanyLogo = ({ uri, size = 32 }) => {
  if (uri) {
    return (
      <Image
        source={{ uri }}
        style={{ width: size, height: size, borderRadius: 6 }}
        resizeMode="contain"
      />
    );
  }
  return (
    <View style={{ width: size, height: size, borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
      <MaterialCommunityIcons name="office-building" size={size * 0.6} color="#fff" />
    </View>
  );
};

const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const getH = async () => { const tok = await AsyncStorage.getItem('auth_token').catch(()=>null); return { 'Content-Type':'application/json','x-api-key':API_KEY, ...(tok?{Authorization:`Bearer ${tok}`}:{}) }; };

const NotificationSettingsScreen = ({ navigation }) => {
  // Notification preferences state
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    AsyncStorage.getItem('company_profile').then(raw => {
      if (!raw) return;
      try {
        const p = JSON.parse(raw);
        const logo = p.logo || p.logo_url || p.company_logo || p.banner_image_url;
        if (logo) setCompanyLogo(logo.startsWith('http') || logo.startsWith('data:') ? logo : `https://api.jobeng.ai${logo}`);
      } catch {}
    }).catch(() => {});
  }, []);

  const [pushNotifications, setPushNotifications] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);

  // Campaign notification types
  const [campaignPerformance, setCampaignPerformance] = useState(true);
  const [budgetAlerts, setBudgetAlerts] = useState(true);
  const [campaignStatus, setCampaignStatus] = useState(true);
  const [assetApproval, setAssetApproval] = useState(true);
  const [campaignCompletion, setCampaignCompletion] = useState(true);

  // System notification types
  const [systemUpdates, setSystemUpdates] = useState(true);
  const [securityAlerts, setSecurityAlerts] = useState(true);
  const [audienceInsights, setAudienceInsights] = useState(false);
  const [maintenanceNotices, setMaintenanceNotices] = useState(true);

  // Timing preferences
  const [quietHours, setQuietHours] = useState(false);
  const [weekendNotifications, setWeekendNotifications] = useState(true);
  const [instantNotifications, setInstantNotifications] = useState(true);
  const [dailyDigest, setDailyDigest] = useState(false);
  const [weeklyReport, setWeeklyReport] = useState(true);

  // Priority settings
  const [highPriorityOnly, setHighPriorityOnly] = useState(false);
  const [actionRequiredOnly, setActionRequiredOnly] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const saved = await AsyncStorage.getItem('notification_settings').catch(()=>null);
        if (saved) {
          const s = JSON.parse(saved);
          if (s.push_notifications !== undefined) setPushNotifications(s.push_notifications);
          if (s.email_notifications !== undefined) setEmailNotifications(s.email_notifications);
          if (s.sms_notifications !== undefined) setSmsNotifications(s.sms_notifications);
          if (s.campaign_performance !== undefined) setCampaignPerformance(s.campaign_performance);
          if (s.budget_alerts !== undefined) setBudgetAlerts(s.budget_alerts);
          if (s.campaign_status !== undefined) setCampaignStatus(s.campaign_status);
        }
      } catch {}
    })();
  }, []);


  useEffect(() => {
    // Mark as having unsaved changes when any setting changes
    setHasUnsavedChanges(true);
  }, [
    pushNotifications,
    emailNotifications,
    smsNotifications,
    campaignPerformance,
    budgetAlerts,
    campaignStatus,
    assetApproval,
    campaignCompletion,
    systemUpdates,
    securityAlerts,
    audienceInsights,
    maintenanceNotices,
    quietHours,
    weekendNotifications,
    instantNotifications,
    dailyDigest,
    weeklyReport,
    highPriorityOnly,
    actionRequiredOnly,
  ]);

  const handleSaveSettings = () => {
    // Simulate saving settings
    Alert.alert(
      'Settings Saved',
      'Your notification preferences have been updated successfully.',
      [{ text: 'OK' }]
    );
    setHasUnsavedChanges(false);
  };

  const handleResetToDefaults = () => {
    Alert.alert(
      'Reset to Defaults',
      'Are you sure you want to reset all notification settings to their default values?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset',
          style: 'destructive',
          onPress: () => {
            // Reset to default values
            setPushNotifications(true);
            setEmailNotifications(true);
            setSmsNotifications(false);
            setCampaignPerformance(true);
            setBudgetAlerts(true);
            setCampaignStatus(true);
            setAssetApproval(true);
            setCampaignCompletion(true);
            setSystemUpdates(true);
            setSecurityAlerts(true);
            setAudienceInsights(false);
            setMaintenanceNotices(true);
            setQuietHours(false);
            setWeekendNotifications(true);
            setInstantNotifications(true);
            setDailyDigest(false);
            setWeeklyReport(true);
            setHighPriorityOnly(false);
            setActionRequiredOnly(false);
          },
        },
      ]
    );
  };

  const handleTestNotification = () => {
    Alert.alert(
      'Test Notification Sent',
      'A test notification has been sent to your enabled channels. Please check your devices.',
      [{ text: 'OK' }]
    );
  };

  const renderSettingRow = (
    title,
    description,
    value,
    onValueChange,
    icon,
    iconColor = '#0e4e96'
  ) => (
    <View style={styles.settingRow}>
      <View style={styles.settingIcon}>
        <MaterialCommunityIcons name={icon} size={24} color={iconColor} />
      </View>
      <View style={styles.settingContent}>
        <Text style={styles.settingTitle}>{title}</Text>
        <Text style={styles.settingDescription}>{description}</Text>
      </View>
      <Switch
        value={value}
        onValueChange={onValueChange}
        trackColor={{ false: '#e1e1e1', true: '#0e4e96' }}
        thumbColor={value ? '#fff' : '#f4f3f4'}
      />
    </View>
  );

  const renderSectionHeader = (title, description) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {description && (
        <Text style={styles.sectionDescription}>{description}</Text>
      )}
    </View>
  );

  const renderActionButton = (title, onPress, style = {}, textStyle = {}) => (
    <TouchableOpacity style={[styles.actionButton, style]} onPress={onPress}>
      <Text style={[styles.actionButtonText, textStyle]}>{title}</Text>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <CompanyLogo uri={companyLogo} size={30} />
        <TouchableOpacity onPress={handleTestNotification}>
          <MaterialCommunityIcons name="bell-ring" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Title */}
      <View style={styles.titleContainer}>
        <Text style={styles.screenTitle}>Notification Settings</Text>
        <Text style={styles.screenSubtitle}>
          Customize how and when you receive notifications
        </Text>
      </View>

      <ScrollView
        style={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Delivery Methods */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Delivery Methods',
            'Choose how you want to receive notifications'
          )}

          {renderSettingRow(
            'Push Notifications',
            'Receive notifications on your mobile device',
            pushNotifications,
            setPushNotifications,
            'cellphone-message'
          )}

          {renderSettingRow(
            'Email Notifications',
            'Receive notifications via email',
            emailNotifications,
            setEmailNotifications,
            'email-outline'
          )}

          {renderSettingRow(
            'SMS Notifications',
            'Receive critical notifications via text message',
            smsNotifications,
            setSmsNotifications,
            'message-text-outline'
          )}
        </View>

        {/* Campaign Notifications */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Campaign Notifications',
            'Stay updated on your campaign performance and status'
          )}

          {renderSettingRow(
            'Performance Alerts',
            'Notifications about campaign performance milestones',
            campaignPerformance,
            setCampaignPerformance,
            'trending-up',
            '#28a745'
          )}

          {renderSettingRow(
            'Budget Alerts',
            'Warnings when campaigns approach budget limits',
            budgetAlerts,
            setBudgetAlerts,
            'currency-gbp',
            '#ffc107'
          )}

          {renderSettingRow(
            'Status Updates',
            'Notifications about campaign starts, pauses, and stops',
            campaignStatus,
            setCampaignStatus,
            'play-pause'
          )}

          {renderSettingRow(
            'Asset Approval',
            'Requests for approval of campaign assets',
            assetApproval,
            setAssetApproval,
            'image-outline',
            '#dc3545'
          )}

          {renderSettingRow(
            'Campaign Completion',
            'Notifications when campaigns finish',
            campaignCompletion,
            setCampaignCompletion,
            'check-circle',
            '#28a745'
          )}
        </View>

        {/* System Notifications */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'System Notifications',
            'Updates about the platform and your account'
          )}

          {renderSettingRow(
            'System Updates',
            'New features and platform improvements',
            systemUpdates,
            setSystemUpdates,
            'update'
          )}

          {renderSettingRow(
            'Security Alerts',
            'Important security and account notifications',
            securityAlerts,
            setSecurityAlerts,
            'shield-checkmark',
            '#dc3545'
          )}

          {renderSettingRow(
            'Audience Insights',
            'Weekly insights about your audience trends',
            audienceInsights,
            setAudienceInsights,
            'account-group'
          )}

          {renderSettingRow(
            'Maintenance Notices',
            'Scheduled maintenance and downtime alerts',
            maintenanceNotices,
            setMaintenanceNotices,
            'wrench'
          )}
        </View>

        {/* Timing Preferences */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Timing Preferences',
            'Control when you receive notifications'
          )}

          {renderSettingRow(
            'Quiet Hours',
            'Pause non-urgent notifications from 10 PM to 8 AM',
            quietHours,
            setQuietHours,
            'sleep'
          )}

          {renderSettingRow(
            'Weekend Notifications',
            'Receive notifications on weekends',
            weekendNotifications,
            setWeekendNotifications,
            'calendar-weekend'
          )}

          {renderSettingRow(
            'Instant Notifications',
            'Receive notifications immediately when events occur',
            instantNotifications,
            setInstantNotifications,
            'lightning-bolt'
          )}

          {renderSettingRow(
            'Daily Digest',
            'Receive a summary of daily activity via email',
            dailyDigest,
            setDailyDigest,
            'email-newsletter'
          )}

          {renderSettingRow(
            'Weekly Report',
            'Receive a weekly performance report',
            weeklyReport,
            setWeeklyReport,
            'chart-line'
          )}
        </View>

        {/* Priority Filters */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Priority Filters',
            'Filter notifications by importance level'
          )}

          {renderSettingRow(
            'High Priority Only',
            'Only receive high priority notifications',
            highPriorityOnly,
            setHighPriorityOnly,
            'alert-circle',
            '#dc3545'
          )}

          {renderSettingRow(
            'Action Required Only',
            'Only receive notifications that require your action',
            actionRequiredOnly,
            setActionRequiredOnly,
            'hand-pointing-up'
          )}
        </View>

        {/* Quick Actions */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Quick Actions',
            'Manage your notification preferences'
          )}

          <View style={styles.quickActionsContainer}>
            <TouchableOpacity
              style={styles.quickActionCard}
              onPress={() => navigation.navigate('NotificationHistoryCampaign')}
            >
              <MaterialCommunityIcons
                name="history"
                size={32}
                color="#0e4e96"
              />
              <Text style={styles.quickActionTitle}>Notification History</Text>
              <Text style={styles.quickActionDescription}>
                View your past notifications
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.quickActionCard}
              onPress={() => navigation.navigate('NotificationScheduleCampaign')}
            >
              <MaterialCommunityIcons
                name="calendar-clock"
                size={32}
                color="#0e4e96"
              />
              <Text style={styles.quickActionTitle}>Schedule Settings</Text>
              <Text style={styles.quickActionDescription}>
                Customize quiet hours and timing
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtonsContainer}>
          {renderActionButton(
            'Test Notification',
            handleTestNotification,
            styles.testButton,
            styles.testButtonText
          )}

          {renderActionButton(
            'Reset to Defaults',
            handleResetToDefaults,
            styles.resetButton,
            styles.resetButtonText
          )}

          {renderActionButton(
            hasUnsavedChanges ? 'Save Changes' : 'Settings Saved',
            handleSaveSettings,
            [styles.saveButton, !hasUnsavedChanges && styles.savedButton],
            [
              styles.saveButtonText,
              !hasUnsavedChanges && styles.savedButtonText,
            ]
          )}
        </View>

        {/* Help Text */}
        <View style={styles.helpContainer}>
          <MaterialCommunityIcons
            name="information-outline"
            size={20}
            color="#666"
          />
          <Text style={styles.helpText}>
            Changes to notification settings may take a few minutes to take
            effect. Critical security notifications cannot be disabled.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleContainer: {
    backgroundColor: '#fff',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
  },
  screenTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  screenSubtitle: {
    fontSize: 16,
    color: '#666',
  },
  contentContainer: {
    flex: 1,
  },
  sectionContainer: {
    backgroundColor: '#fff',
    margin: 15,
    marginBottom: 0,
    borderRadius: 8,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  sectionHeader: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  settingIcon: {
    marginRight: 15,
  },
  settingContent: {
    flex: 1,
    marginRight: 15,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 18,
  },
  quickActionsContainer: {
    flexDirection: 'row',
    gap: 15,
  },
  quickActionCard: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    padding: 20,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e1e1e1',
  },
  quickActionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 10,
    marginBottom: 5,
    textAlign: 'center',
  },
  quickActionDescription: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    lineHeight: 16,
  },
  actionButtonsContainer: {
    padding: 15,
    gap: 10,
  },
  actionButton: {
    backgroundColor: '#0e4e96',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  testButton: {
    backgroundColor: '#17a2b8',
  },
  testButtonText: {
    color: '#fff',
  },
  resetButton: {
    backgroundColor: '#6c757d',
  },
  resetButtonText: {
    color: '#fff',
  },
  saveButton: {
    backgroundColor: '#28a745',
  },
  saveButtonText: {
    color: '#fff',
  },
  savedButton: {
    backgroundColor: '#e9ecef',
  },
  savedButtonText: {
    color: '#6c757d',
  },
  helpContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#f8f9fa',
    padding: 15,
    margin: 15,
    marginTop: 0,
    borderRadius: 8,
  },
  helpText: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    marginLeft: 10,
    flex: 1,
  },
});

export default NotificationSettingsScreen;
